import React, { useState, useEffect } from 'react';
import { noop } from 'lodash';
import { Slider } from 'antd';

/**
 * CITSlider component.
 *
 * @param {Object} props - Props for the CITSlider component.
 * @param {SetStateAction} props.onChange - The function to be called when the slider value changes.
 * @param {Boolean} props.disabled - A flag indicating whether the slider is disabled.
 * @param {number} [props.defaultValue] - The default value of the slider.
 * @param {number} [props.min] - The minimum value of the slider.
 * @param {number} [props.max] - The maximum value of the slider.
 * @param {Object} [props.marks] - The marks to be displayed on the slider.
 * @param {Function} [props.tipFormatter] - The function to format the tooltip value.
 * @param {Boolean} [props.clear] - A flag indicating whether to clear the slider value.
 * @param {Object} [props] - Additional props to be spread for the Slider component.
 * @returns {JSX.Element} The CITSlider component.
 * @example
 *  <CITSlider
        disabled={isEmptyDate}
        onChange={setCutoffPercent}
    />
 * 
 */
function CITSlider({
    defaultValue,
    min,
    max,
    marks,
    tipFormatter,
    onChange,
    clear,
    disabled,
    ...props
}) {
    const [value, setValue] = useState(1);

    const updateValue = () => {
        /**
         * - If `clear` is `true`, set filter value to defaultValue
         * - If not, change value according to user triggered action
         */
        if (clear) {
            onChange();
            setValue(defaultValue);
        } else {
            onChange(value);
        }
    }

    useEffect(() => {
        updateValue();
    }, []);

    return (
        <Slider
            min={min}
            max={max}
            marks={marks}
            onChange={setValue}
            onAfterChange={updateValue}
            defaultValue={defaultValue}
            value={clear ? [] : value}
            tipFormatter={tipFormatter}
            step={0.01}
            disabled={disabled}
            {...props}
        />
    );
}

CITSlider.defaultProps = {
    defaultValue: 1,
    min: 0.01,
    max: 1.0,
    marks: { 0.01: '0.01', 0.3: '0.3', 0.7: '0.7', 1.0: '1.0' },
    onChange: noop,
    clear: false,
    disabled: false
};

export default CITSlider;